import { useState } from 'react';
import type { Sprint, TicketListItem } from '../../api/types';
import { Notice } from '../../components/ui/Notice';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import type { Version } from '../versions/versions-api';
import {
  ASSIGNEE_MAX,
  DESCRIPTION_MAX,
  NONE,
  POINTS_HINT,
  TITLE_MAX,
  blankDraft,
  draftOf,
  draftProblem,
  type TicketDraft,
} from './ticket-form';

/**
 * CREATE OR EDIT A TICKET — PH-57's form.
 *
 * 🔴 THE TICKET: *"new tickets on PH not have create new tickets in UI planhub by human"*. Both
 * halves were missing, and edit was the quieter one: the only ticket PATCH this app made was
 * `/rank`, so nobody could change a title, an assignee or an estimate either. Or asked me to assign
 * and estimate the sprint by hand earlier today — both of those are `PATCH /tickets/:id`. He was
 * asking because he could not do it himself, and neither of us said so out loud.
 *
 * 🔴 MOBILE FIRST, 390px — same bottom-sheet shape as `SprintFormDialog` and `CompleteSprintDialog`,
 * deliberately: `items-end` and full width on a phone, a centred dialog from `sm:` up, every control
 * 44px tall, and the paired fields STACK below `sm:` rather than sharing a row at ~170px each.
 *
 * ⛔ NO STATUS FIELD, AND ITS ABSENCE IS EXPLAINED ON THE FORM rather than left to be discovered.
 * `PATCH` refuses `status` with a 400 that fails the whole request; status moves through the status
 * dropdown, which writes a history row. A form that offered status would 400 every save.
 *
 * ⛔ SUBMIT VALIDATES AND CAN WRITE NOTHING, and the button is deliberately NOT disabled so that the
 * guard is reachable and therefore testable. A greyed-out Save with no explanation is the "it did
 * nothing" failure this codebase refuses.
 */

export type TicketFormMode =
  | { kind: 'create'; projectId: string; sprintId?: string | null; parentId?: string | null }
  | { kind: 'edit'; ticket: TicketListItem };

const FIELD_CLASS =
  'mt-1 h-11 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 text-sm disabled:opacity-50';
const LABEL_CLASS = 'block text-xs font-semibold';

export function TicketFormDialog({
  mode,
  sprints,
  versions,
  busy,
  error,
  problem: outerProblem = null,
  onCancel,
  onSubmit,
}: {
  mode: TicketFormMode;
  sprints: readonly Sprint[];
  versions: readonly Version[];
  busy: boolean;
  /** The API's own words for a refusal — something WAS sent. */
  error: string | null;
  /**
   * A refusal from the CALLER that never reached the API — today, "nothing has changed".
   *
   * ⛔ It shares the dialog's own `problem` channel rather than `error`, because both mean the same
   * thing to a reader: nothing was sent, and pressing Save again unchanged will do the same. Routing
   * it through `error` put "The API refused this ticket" above a request the API never received.
   */
  problem?: string | null;
  onCancel: () => void;
  onSubmit: (draft: TicketDraft) => void;
}) {
  const vocab = useVocabulary();
  const editing = mode.kind === 'edit' ? mode.ticket : null;

  // ⭐ Initialised ONCE per open — the dialog is unmounted between opens, so there is no stale draft
  //   to reset and no effect to write one. Same division as `SprintFormDialog`.
  const [draft, setDraft] = useState<TicketDraft>(() =>
    editing
      ? draftOf(editing)
      : blankDraft({
          sprintId: mode.kind === 'create' ? (mode.sprintId ?? null) : null,
          type: vocab.types[0]?.code,
        }),
  );
  const [ownProblem, setOwnProblem] = useState<string | null>(null);
  // ⭐ OURS wins while it is set: it names a field the reader can fix right now, whereas the caller's
  //   is about the form as a whole. Both render in the same place under the same heading.
  const problem = ownProblem ?? outerProblem;

  const set = <K extends keyof TicketDraft>(key: K, value: TicketDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    // Clearing our own complaint: a message about a field the reader has since fixed reads as a
    // fresh refusal.
    setOwnProblem(null);
  };

  const submit = () => {
    const found = draftProblem(draft);
    setOwnProblem(found);
    // ⛔ THE ASSERTION THIS EXISTS FOR: an invalid draft writes NOTHING and does not travel to the
    //    API to come back as a generic `the request body or query was not valid`.
    if (found) return;
    onSubmit(draft);
  };

  const title = editing ? `Edit ${editing.key}` : 'New ticket';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-full w-full max-w-2xl space-y-3 overflow-y-auto rounded-t-lg border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4 sm:rounded-lg"
      >
        <h2 className="text-base font-semibold">{title}</h2>

        <div>
          <label className={LABEL_CLASS} htmlFor="ticket-form-title">
            Title
          </label>
          <input
            id="ticket-form-title"
            type="text"
            value={draft.title}
            maxLength={TITLE_MAX}
            onChange={(event) => set('title', event.target.value)}
            disabled={busy}
            autoFocus
            placeholder="What needs doing?"
            className={FIELD_CLASS}
          />
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="ticket-form-description">
            Description{' '}
            <span className="font-normal text-[color:var(--color-ink-muted)]">(optional)</span>
          </label>
          <textarea
            id="ticket-form-description"
            value={draft.description}
            maxLength={DESCRIPTION_MAX}
            onChange={(event) => set('description', event.target.value)}
            disabled={busy}
            rows={6}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm disabled:opacity-50"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className={LABEL_CLASS} htmlFor="ticket-form-type">
              Type
            </label>
            <select
              id="ticket-form-type"
              value={draft.type}
              onChange={(event) => set('type', event.target.value)}
              disabled={busy}
              className={FIELD_CLASS}
            >
              <option value="">Choose…</option>
              {vocab.types.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={LABEL_CLASS} htmlFor="ticket-form-priority">
              Priority
            </label>
            <select
              id="ticket-form-priority"
              value={draft.priority}
              onChange={(event) => set('priority', event.target.value)}
              disabled={busy}
              className={FIELD_CLASS}
            >
              {/* ⭐ On CREATE this means "let the API pick its default" — `priority` is optional on
                  create, so an omitted key is not a missing value, it is the project's default. */}
              <option value="">{editing ? 'Unchanged' : 'Default'}</option>
              {vocab.priorities.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={LABEL_CLASS} htmlFor="ticket-form-impact">
              Impact{' '}
              <span className="font-normal text-[color:var(--color-ink-muted)]">(optional)</span>
            </label>
            <select
              id="ticket-form-impact"
              value={draft.impact}
              onChange={(event) => set('impact', event.target.value)}
              disabled={busy}
              className={FIELD_CLASS}
            >
              <option value={NONE}>None</option>
              {vocab.impacts.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={LABEL_CLASS} htmlFor="ticket-form-points">
              Story points{' '}
              <span className="font-normal text-[color:var(--color-ink-muted)]">(optional)</span>
            </label>
            <input
              id="ticket-form-points"
              type="text"
              inputMode="decimal"
              value={draft.storyPoints}
              onChange={(event) => set('storyPoints', event.target.value)}
              disabled={busy}
              placeholder="1"
              className={FIELD_CLASS}
            />
            {/* ⭐ Or's convention, said on the form. An estimate nobody can interpret is worse than
                no estimate, and "3" meaning difficulty vs three days is the ambiguity it removes.
                ⛔ `type="text"` with `inputMode="decimal"`, NOT `type="number"` — a number input
                   silently yields '' for an unparseable entry, so a typo would read as "cleared"
                   and PATCH the estimate to null. Here a bad value survives to `pointsProblem`,
                   which says what is wrong with it. */}
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">{POINTS_HINT}</p>
          </div>

          <div>
            <label className={LABEL_CLASS} htmlFor="ticket-form-assignee">
              Assignee{' '}
              <span className="font-normal text-[color:var(--color-ink-muted)]">(optional)</span>
            </label>
            <input
              id="ticket-form-assignee"
              type="text"
              value={draft.assignee}
              maxLength={ASSIGNEE_MAX}
              onChange={(event) => set('assignee', event.target.value)}
              disabled={busy}
              placeholder="GitLab username or email"
              className={FIELD_CLASS}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={LABEL_CLASS} htmlFor="ticket-form-sprint">
              Sprint{' '}
              <span className="font-normal text-[color:var(--color-ink-muted)]">(optional)</span>
            </label>
            <select
              id="ticket-form-sprint"
              value={draft.sprintId}
              onChange={(event) => set('sprintId', event.target.value)}
              disabled={busy}
              className={FIELD_CLASS}
            >
              <option value={NONE}>Backlog (no sprint)</option>
              {sprints.map((sprint) => (
                <option key={sprint.id} value={sprint.id}>
                  {sprint.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={LABEL_CLASS} htmlFor="ticket-form-version">
              Release{' '}
              <span className="font-normal text-[color:var(--color-ink-muted)]">(optional)</span>
            </label>
            <select
              id="ticket-form-version"
              value={draft.versionId}
              onChange={(event) => set('versionId', event.target.value)}
              disabled={busy}
              className={FIELD_CLASS}
            >
              <option value={NONE}>No release</option>
              {versions.map((version) => (
                <option key={version.id} value={version.id}>
                  {version.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={LABEL_CLASS} htmlFor="ticket-form-start">
              Start date{' '}
              <span className="font-normal text-[color:var(--color-ink-muted)]">(optional)</span>
            </label>
            <input
              id="ticket-form-start"
              type="date"
              value={draft.startDate}
              onChange={(event) => set('startDate', event.target.value)}
              disabled={busy}
              className={FIELD_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="ticket-form-end">
              End date{' '}
              <span className="font-normal text-[color:var(--color-ink-muted)]">(optional)</span>
            </label>
            <input
              id="ticket-form-end"
              type="date"
              value={draft.endDate}
              onChange={(event) => set('endDate', event.target.value)}
              disabled={busy}
              className={FIELD_CLASS}
            />
          </div>
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="ticket-form-modules">
            Modules{' '}
            <span className="font-normal text-[color:var(--color-ink-muted)]">
              (optional, comma-separated)
            </span>
          </label>
          <input
            id="ticket-form-modules"
            type="text"
            value={draft.modules}
            onChange={(event) => set('modules', event.target.value)}
            disabled={busy}
            placeholder="api, web"
            className={FIELD_CLASS}
          />
        </div>

        {/* ⛔ SAID OUT LOUD RATHER THAN LEFT AS AN ABSENCE. A reader who came here to move a ticket
            to Done needs to know where that lives — otherwise the missing field reads as a bug and
            the next ticket filed is a duplicate of one that was closed on purpose. */}
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          Status is not edited here — use the status control on the ticket, which records who moved
          it and when. This form cannot change status even if asked to.
        </p>

        {/* ⚠️ TWO SOURCES OF "NO", KEPT APART: `problem` is OUR refusal and nothing was sent;
            `error` is the API's and something was. A reader who cannot tell them apart cannot tell
            whether pressing Save again might work. */}
        {problem ? (
          <Notice tone="warning" title="This ticket was not sent">
            <p className="break-words">{problem}</p>
          </Notice>
        ) : null}

        {error ? (
          <Notice tone="error" title="The API refused this ticket">
            <p className="break-words">{error}</p>
          </Notice>
        ) : null}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="h-11 rounded border border-[color:var(--color-line)] px-3 text-sm disabled:opacity-50 sm:h-9"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="h-11 rounded bg-[color:var(--color-accent)] px-4 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50 sm:h-9"
          >
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Create ticket'}
          </button>
        </div>
      </div>
    </div>
  );
}
